import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Instagram, Menu, X, Info, Building2, Star } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GREEN = "#2F6B4F";
const GREEN_DARK = "#1F4A36";
const CORAL = "#E2734F";
const CREAM = "#FBF8F1";
const NAME = "Baroquiña Dental Clinic";
const FB = "https://www.facebook.com/baroquinadentalclinic/";
const IG = "https://www.instagram.com/baroquinadentalclinic/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function BaroquinaConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Services", "#services"], ["Branches", "#branches"], ["Reviews", "#reviews"], ["Contact", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#26301F", fontFamily: "'Nunito', sans-serif" }} data-testid="baroquina-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600&family=Nunito:wght@400;500;600;700&display=swap');
        .brq-round { font-family: 'Fredoka', sans-serif; }
      `}</style>

      <div data-testid="dental-disclaimer" className="bg-gray-900 text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="brq-logo" className="flex items-center gap-3">
            <img src="/dental/baroquina-dental-concept/logo.jpg" alt={NAME} className="h-12 w-12 rounded-full object-cover" />
            <span className="brq-round text-lg" style={{ color: GREEN_DARK }}>{NAME}</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-semibold text-[#3A4A34] hover:text-[#2F6B4F] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="brq-header-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: CORAL }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="brq-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-semibold text-[#26301F]">{l}</a>)}</div>}
      </header>

      {/* HERO — split, rounded, family-warm */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-10 items-center">
        <motion.div {...fadeUp} className="order-2 md:order-1">
          <span className="inline-block rounded-full px-4 py-1.5 text-xs font-bold text-white mb-5" style={{ background: GREEN }}>Two Locations in Bacolod</span>
          <h1 className="text-5xl sm:text-6xl brq-round leading-[1.1]" style={{ color: GREEN_DARK }}>Affordable, Friendly Family Dentistry</h1>
          <p className="mt-5 text-lg text-[#3A4A34]/80 leading-relaxed">From your child's first check-up to your own smile makeover — Baroquiña Dental Clinic makes dental visits easy for the whole family.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="https://seriousmd.com/doc/sheramil-baroquia" target="_blank" rel="noopener noreferrer" data-testid="brq-hero-book" className="rounded-full px-8 py-3.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: CORAL }}>Book an Appointment</a>
            <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="brq-hero-message" className="rounded-full px-8 py-3.5 text-sm font-bold border-2" style={{ borderColor: GREEN, color: GREEN_DARK }}>Message Us</a>
          </div>
          <p className="mt-7 text-sm font-semibold text-[#3A4A34]/70"><Star size={14} className="inline -mt-0.5" style={{ color: CORAL }} /> 4.8 rating · 42 reviews</p>
        </motion.div>
        <motion.div {...fadeUp} className="order-1 md:order-2 rounded-[2rem] overflow-hidden shadow-xl">
          <img src="/dental/baroquina-dental-concept/team-storefront.jpg" alt="Dra. Sheramil Baroquiña and the team" className="w-full h-96 object-cover" />
        </motion.div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.h2 {...fadeUp} className="text-3xl brq-round" style={{ color: GREEN_DARK }}>Our Practice</motion.h2>
        <motion.p {...fadeUp} className="mt-5 text-lg text-[#3A4A34]/80 leading-relaxed">
          At Baroquiña Dental Clinic, we aim to make dental care approachable, comfortable and easy to understand for every member of the family — at a price that keeps care within reach.
        </motion.p>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-16" style={{ background: "#EFF6EE" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-3xl brq-round text-center mb-12" style={{ color: GREEN_DARK }}>What We Offer</motion.h2>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div {...fadeUp} className="rounded-[2rem] overflow-hidden shadow-lg">
              <img src="/dental/baroquina-dental-concept/kids.jpg" alt="Gentle dental care for kids" className="w-full h-80 object-cover" />
            </motion.div>
            <motion.div {...fadeUp}>
              <h3 className="text-2xl brq-round" style={{ color: GREEN_DARK }}>Gentle Care for Kids</h3>
              <p className="mt-3 text-[#3A4A34]/80 leading-relaxed">Pediatric dental care designed to make young patients feel safe and even look forward to their next visit.</p>
              <div className="mt-8 border-t pt-6" style={{ borderColor: "#D9E5D3" }}>
                {["General Dentistry", "Cosmetic Dentistry", "Tooth Extraction"].map((s) => (
                  <div key={s} className="flex items-center gap-3 py-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: CORAL }} />
                    <span className="font-semibold text-[#26301F]">{s}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRANCHES */}
      <section id="branches" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="text-3xl brq-round text-center mb-3" style={{ color: GREEN_DARK }}>Two Convenient Branches</motion.h2>
        <motion.p {...fadeUp} className="text-center text-[#3A4A34]/70 mb-12">Wherever you are in Bacolod, we're never far away.</motion.p>
        <div className="grid sm:grid-cols-2 gap-8">
          {[{ name: "Rosario Branch", note: "Corner Araneta & Rosario St., Bacolod City" }, { name: "Lacson Branch", note: "15th Street, Lacson, Bacolod City" }].map((b) => (
            <motion.div key={b.name} {...fadeUp} data-testid="brq-branch" className="rounded-[2rem] bg-white p-8 shadow-md">
              <Building2 size={22} style={{ color: GREEN }} />
              <h3 className="mt-3 text-xl brq-round" style={{ color: GREEN_DARK }}>{b.name}</h3>
              <p className="mt-2 text-[#3A4A34]/70">{b.note}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TEAM + PREVENTIVE */}
      <section className="py-16" style={{ background: "#EFF6EE" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-8">
          <motion.div {...fadeUp} className="rounded-[2rem] overflow-hidden shadow-md"><img src="/dental/baroquina-dental-concept/team.jpg" alt="The Baroquiña Dental Clinic team" className="w-full h-72 object-cover" /><p className="p-5 font-semibold text-[#26301F]">Our friendly team, ready to welcome you</p></motion.div>
          <motion.div {...fadeUp} className="rounded-[2rem] overflow-hidden shadow-md"><img src="/dental/baroquina-dental-concept/preventive.jpg" alt="Preventive cleaning and check-ups" className="w-full h-72 object-cover" /><p className="p-5 font-semibold text-[#26301F]">Preventive cleaning and regular check-ups</p></motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="text-3xl brq-round text-center mb-12" style={{ color: GREEN_DARK }}>What Our Patients Say</motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { text: "Had a painful impacted tooth. Dr. Luwela made me calm and comfortable — the extraction was unexpectedly smooth and painless.", author: "Beah Artagame" },
            { text: "10/10 experience. Dra. Luwela cared for both me and my children, explained everything clearly.", author: "Jessa Pahilanga" },
            { text: "Highly recommended, very affordable while offering high-quality service.", author: "Facebook testimonial" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`brq-testimonial-${i}`} className="rounded-[2rem] bg-white p-7 shadow-md">
              <p className="text-[#3A4A34]/85 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 font-bold" style={{ color: GREEN_DARK }}>{q.author}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center" style={{ background: GREEN_DARK }}>
        <h2 className="text-3xl brq-round text-white">Let's get your family smiling</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a href="https://seriousmd.com/doc/sheramil-baroquia" target="_blank" rel="noopener noreferrer" data-testid="brq-cta-book" className="rounded-full px-8 py-3.5 text-sm font-bold text-white" style={{ background: CORAL }}>Book Online</a>
          <a href="tel:+639611208978" data-testid="brq-cta-call" className="rounded-full px-8 py-3.5 text-sm font-bold text-white border-2 border-white/40">Call the Clinic</a>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="text-3xl brq-round" style={{ color: GREEN_DARK }}>Visit Us</h2>
          <ul className="mt-7 space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GREEN }} className="mt-0.5" /><span>Corner Araneta &amp; Rosario St., Bacolod City</span></li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: GREEN }} className="mt-0.5" /><a href="tel:+639611208978" className="hover:underline">+63 961 120 8978</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GREEN }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="brq-facebook-link" className="hover:underline">baroquinadentalclinic</a></li>
            <li className="flex items-start gap-4"><Instagram size={20} style={{ color: GREEN }} className="mt-0.5" /><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="brq-instagram-link" className="hover:underline">baroquinadentalclinic</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="rounded-[2rem] overflow-hidden min-h-[320px] shadow-md">
          <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Araneta Rosario St Bacolod City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </motion.div>
      </section>

      <footer className="py-10" style={{ background: GREEN_DARK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p className="brq-round text-white text-base">Baroquiña Dental Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: CORAL }}>Message Baroquiña</a>
      <ConceptCTAPill slug="baroquina-dental-concept" />
    </div>
  );
}
